import React, { useState, useMemo } from 'react';
import { Box, Checkbox, InputAdornment, TextField, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import { ModalHeaderTitle, ModalFooter } from './GroupLessonTemplateStyled';

// 한글 초성 검색
const CHO = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const getChosung = str => [...str].map(c => {
  const code = c.charCodeAt(0) - 0xAC00;
  return code >= 0 && code <= 11171 ? CHO[Math.floor(code / 588)] : c;
}).join('');
const isChosung = str => [...str].every(c => CHO.includes(c));
const matchSearch = (name, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  if (name.toLowerCase().includes(q)) return true;
  if (isChosung(q)) return getChosung(name).includes(q);
  return false;
};
const getSortGroup = ch => {
  if (/[가-힣ㄱ-ㅎ]/.test(ch)) return 0;
  if (/[a-zA-Z]/.test(ch)) return 1;
  if (/[0-9]/.test(ch)) return 2;
  return 3;
};
const sortTickets = (a, b) => {
  const ga = getSortGroup(a.courseTicketName[0] || '');
  const gb = getSortGroup(b.courseTicketName[0] || '');
  if (ga !== gb) return ga - gb;
  return a.courseTicketName.localeCompare(b.courseTicketName, 'ko');
};

const TicketSelectModal = ({ open, onClose, tickets = [], selectedIds = [], onChangeIds }) => {
  const [search, setSearch] = useState('');

  const ticketSearchText = t => `${t.courseTicketName} ${t.usageCount || ''}회 ${t.usagePeriodDisplay || ''}`;
  const filteredList = (tickets?.filter(t => matchSearch(ticketSearchText(t), search)) || []).sort(sortTickets);

  const listWithHeaders = useMemo(() => {
    const result = [];
    let lastCho = '';
    filteredList.forEach(ticket => {
      const firstChar = ticket.courseTicketName[0] || '';
      const cho = getChosung(firstChar);
      if (cho !== lastCho) {
        result.push({ type: 'header', label: cho });
        lastCho = cho;
      }
      result.push({ type: 'ticket', ticket });
    });
    return result;
  }, [filteredList]);

  const selectedCount = selectedIds.length;
  const totalCount = tickets?.length || 0;
  const allSelected = totalCount > 0 && selectedCount === totalCount;

  const toggleTicket = ticketId => {
    onChangeIds(
      selectedIds.includes(ticketId)
        ? selectedIds.filter(id => id !== ticketId)
        : [...selectedIds, ticketId],
    );
  };

  const toggleAll = () => {
    const targetIds = filteredList.map(t => t.courseTicketId);
    const allFilteredSelected = targetIds.every(id => selectedIds.includes(id));
    onChangeIds(
      allFilteredSelected
        ? selectedIds.filter(id => !targetIds.includes(id))
        : [...new Set([...selectedIds, ...targetIds])],
    );
  };

  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '600px', backgroundColor: '#fff', borderRadius: '10px', overflow: 'hidden',
      }}>
        <Box sx={{
          backgroundColor: '#555', height: '52px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          borderRadius: '10px 10px 0 0', padding: '0 20px',
        }}>
          <ModalHeaderTitle>수강권 선택</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </Box>

        {/* 검색 */}
        <Box sx={{ p: '12px 16px', backgroundColor: '#fafafa', borderBottom: '1px solid #eee' }}>
          <TextField
            size="small" fullWidth
            placeholder="수강권 검색... (초성 검색 가능)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#4AB3BC' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '36px', backgroundColor: '#fff', borderRadius: '6px',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4AB3BC' },
              },
            }}
          />
        </Box>

        {/* 전체 선택 */}
        <Box
          onClick={toggleAll}
          sx={{
            display: 'flex', alignItems: 'center', px: '16px', py: '10px',
            cursor: 'pointer', borderBottom: '1px solid #eee',
            backgroundColor: allSelected ? '#e8f5f5' : '#fff',
            '&:hover': { backgroundColor: allSelected ? '#ddf0f0' : '#f5f5f5' },
          }}>
          <Checkbox
            checked={allSelected}
            indeterminate={selectedCount > 0 && !allSelected}
            size="small"
            sx={{ p: 0, mr: 1.5, color: '#4AB3BC', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } }}
          />
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}>전체 선택</Typography>
          <Typography sx={{ fontSize: '13px', color: '#4AB3BC', fontWeight: 500, ml: 'auto' }}>
            {selectedCount}/{totalCount}
          </Typography>
        </Box>

        {/* 수강권 목록 */}
        <Box sx={{
          maxHeight: '400px', overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: '3px' },
        }}>
          {listWithHeaders.length === 0 ? (
            <Typography sx={{ p: '16px', fontSize: '13px', color: '#999', textAlign: 'center' }}>
              검색 결과가 없습니다
            </Typography>
          ) : (
            listWithHeaders.map((item, idx) => {
              if (item.type === 'header') {
                return (
                  <Box key={`header-${item.label}-${idx}`} sx={{ px: '16px', py: '4px', backgroundColor: '#E8E8E8', position: 'sticky', top: 0, zIndex: 1 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#666', letterSpacing: '1px' }}>{item.label}</Typography>
                  </Box>
                );
              }
              const { ticket } = item;
              const isChecked = selectedIds.includes(ticket.courseTicketId);
              return (
                <Box
                  key={ticket.courseTicketId}
                  onClick={() => toggleTicket(ticket.courseTicketId)}
                  sx={{
                    display: 'flex', alignItems: 'center', px: '16px', py: '8px',
                    cursor: 'pointer',
                    backgroundColor: isChecked ? '#f0f8f8' : 'transparent',
                    borderLeft: isChecked ? '3px solid #4AB3BC' : '3px solid transparent',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'all 0.1s ease',
                    '&:hover': { backgroundColor: isChecked ? '#e8f5f5' : '#f8f8f8' },
                  }}>
                  <Checkbox
                    checked={isChecked}
                    size="small"
                    sx={{ p: 0, mr: 1.5, color: '#c4c4c4', '&.Mui-checked': { color: '#4AB3BC' } }}
                  />
                  <Typography sx={{ fontSize: '14px', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isChecked ? '#333' : '#555' }}>
                    {ticket.courseTicketName}
                    {(ticket.usageCount || ticket.usagePeriodDisplay) && (
                      <span style={{ color: '#aaa', marginLeft: 6, fontSize: '13px' }}>
                        {ticket.usageCount ? `${ticket.usageCount}회` : ''}{ticket.usageCount && ticket.usagePeriodDisplay ? ' · ' : ''}{ticket.usagePeriodDisplay || ''}
                      </span>
                    )}
                  </Typography>
                  {ticket.ticketType === 'TRIAL_TICKET' && (
                    <Typography sx={{ fontSize: '10px', color: '#fff', background: '#4AB3BC', borderRadius: '10px', px: '8px', py: '1px', flexShrink: 0, fontWeight: 500 }}>
                      일일권
                    </Typography>
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* 푸터 */}
        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            취소
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={onClose}>
            선택 완료 ({selectedCount}개)
          </CommonModalButton>
        </ModalFooter>
      </Box>
    </Modal>
  );
};

export default TicketSelectModal;
